import { ApiRetailerPaymentType } from '@vend/types'
import { useCallback } from 'react'
import uuid from '@vend/vend-uuid'
import { subtract, vn } from 'vend-number'
import { trackError } from '@vend/utilities'
import { IntegratedPaymentOption, SalePayment } from '../PaymentModal/types'
import {
  usePaymentManager,
  useRegisterManager,
  useTrainingModeManager,
} from '../../../../ng-compat/hooks'
import { print } from '../../../print/print'
import {
  ExternalPaymentEventData,
  extractExternalPaymentData,
  getAllExternalTransactionIds,
  trackIntegratedPaymentStep,
} from '../../../../functions/payment-types/integrated'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCurrentSaleAsSale,
  selectOriginalSalePayments,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { AcceptStep } from '../../../payments/integrated-payment/integrated-payment-steps.constant'
import { PaymentData } from '../../../payments/integrated-payment/integrated-payment-modal/IntegratedPaymentModal'
import { getAllPaymentsForPaymentType } from '../../../../functions/payments'
import { getTransactions } from '../../../payments/payments-resource.service'
import { isReturn } from '../../../../functions/sale'
import { selectSaleReturnFor } from '../../../../state/selectors'

export const useHandleIntegrationPayment = (amountTendered: string) => {
  const register = useRegisterManager().getActiveRegister()
  const returnFor = useAppSelector(selectSaleReturnFor)
  const originalSalePayments = useAppSelector(selectOriginalSalePayments)

  const handleReturnSalePayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ): Promise<IntegratedPaymentOption> | IntegratedPaymentOption => {
      const originalRegisterSalePaymentIds = getAllPaymentsForPaymentType({
        payments: originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })
        .map(payment => payment.id)
        .filter(data => data !== undefined)
      const integratedPaymentOption: IntegratedPaymentOption = {
        paymentModalType: 'integrated',
        paymentType,
        amount: vn(amountTendered).round(2).toString(),
        registerId: register.id,
        paymentId: uuid(),
      }

      if (originalRegisterSalePaymentIds.length > 0) {
        return getTransactions(register.id, originalRegisterSalePaymentIds)
          .then(transactions => {
            if (transactions.length) {
              return {
                ...integratedPaymentOption,
                externalTransactionIds: getAllExternalTransactionIds(
                  transactions
                ),
                registerSalePaymentIds: originalRegisterSalePaymentIds,
              }
            }
            return integratedPaymentOption
          })
          .catch((error: Error) => {
            trackError(error)
            return integratedPaymentOption
          })
      }
      return integratedPaymentOption
    },
    [amountTendered, originalSalePayments, register.id]
  )

  const handleIntegrationPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ): Promise<IntegratedPaymentOption> | IntegratedPaymentOption => {
      if (isReturn({ returnFor })) {
        return handleReturnSalePayment(paymentType)
      }
      return {
        paymentType,
        paymentModalType: 'integrated',
        registerId: register.id,
        paymentId: uuid(),
        amount: vn(amountTendered).round(2).toString(),
      }
    },
    [amountTendered, handleReturnSalePayment, register.id, returnFor]
  )
  return {
    handleIntegrationPayment,
  }
}

export const usePrintIntegratedPaymentReceipt = () => {
  const paymentManager = usePaymentManager()
  const trainingModeManager = useTrainingModeManager()
  const sale = useAppSelector(selectCurrentSaleAsSale)
  const registerManager = useRegisterManager()
  const outlet = registerManager.getActiveOutlet()
  const register = registerManager.getActiveRegister()

  const printReceipt = useCallback(async () => {
    return print({
      type: 'receipt-settings-and-template',
      data: {
        trainingModeEnabled: trainingModeManager.trainingModeEnabled,
        outlet,
        receiptHtmlExtra: paymentManager.paymentIntegrationReceiptMessage,
        register,
        sale,
      },
    })
  }, [
    outlet,
    paymentManager.paymentIntegrationReceiptMessage,
    register,
    sale,
    trainingModeManager.trainingModeEnabled,
  ])

  return { printReceipt }
}

export const useHandleRejectedIntegratedPayment = () => {
  const { printReceipt } = usePrintIntegratedPaymentReceipt()
  const paymentManager = usePaymentManager()
  const handleRejectedPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType,
      eventData?: ExternalPaymentEventData
    ) => {
      if (eventData && eventData.receipt_html_extra) {
        paymentManager.updateReceiptMessageOnDecline(
          eventData.receipt_html_extra
        )
        printReceipt()
      }
      trackIntegratedPaymentStep({ paymentType, eventData })
    },
    [paymentManager, printReceipt]
  )

  return { handleRejectedPayment }
}

export const useHandleCompleteIntegratedPayment = (
  completePayment: (
    payment: SalePayment,
    clearSelectedPayment?: boolean
  ) => Promise<void>,
  setShowPartialAuthModal: (partialPayment: AcceptStep | undefined) => void,
  setPaymentData: (paymentData: PaymentData) => void,
  tenderedAmount: string
) => {
  const paymentManager = usePaymentManager()
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const outlet = registerManager.getActiveOutlet()
  const handleAcceptedPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType,
      amountTendered: string,
      eventData?: AcceptStep
    ) => {
      if (eventData && eventData.receipt_html_extra) {
        paymentManager.acceptedPaymentIntegrationReceiptMessages.push(
          eventData.receipt_html_extra
        )
      }
      trackIntegratedPaymentStep({ paymentType, eventData })
      let isPartialAuth = false
      let tipAmount = null
      let approvedAmount = tenderedAmount

      // If the approved_amount is less than the tendered amount then
      // they have made a partial payment
      if (
        eventData &&
        eventData.approved_amount &&
        vn(tenderedAmount).gt(vn(eventData.approved_amount))
      ) {
        approvedAmount = eventData.approved_amount
        isPartialAuth = true
      }

      // If the tipping feature is enabled, AND
      // If the approved_amount is more than the tendered amount then
      // we assume they have added a tip to the payment
      if (
        getFeatureFromCache('terminal_tipping') &&
        eventData &&
        eventData.approved_amount &&
        vn(tenderedAmount).lt(vn(eventData.approved_amount))
      ) {
        approvedAmount = eventData.approved_amount
        tipAmount = subtract(eventData.approved_amount, amountTendered)
      }
      return completePayment(
        {
          paymentAmount: vn(approvedAmount).toNumber(),
          tipAmount: tipAmount ?? undefined,
          paymentType,
          transactionData: extractExternalPaymentData(eventData),
          paymentId: eventData?.payment_id,
        },
        !isPartialAuth
      ).then(() => {
        if (!isPartialAuth) {
          return
        }
        setShowPartialAuthModal(eventData!)
      })
    },
    [
      completePayment,
      paymentManager.acceptedPaymentIntegrationReceiptMessages,
      setShowPartialAuthModal,
      tenderedAmount,
    ]
  )

  const handlePartialAuthRejected = useCallback(
    (paymentType: ApiRetailerPaymentType, eventData: AcceptStep) => {
      const externalTransactionIds = []
      if (eventData.external_transaction_id) {
        externalTransactionIds.push(eventData.external_transaction_id)
      }
      const amountTendered = eventData.approved_amount
        ? -eventData.approved_amount
        : -tenderedAmount
      setShowPartialAuthModal(undefined)
      setPaymentData({
        currency: outlet.currency,
        retailerPaymentTypeId: paymentType.id,
        amount: vn(amountTendered).round(2).toString(),
        paymentType,
        registerId: register.id,
        externalTransactionIds,
        registerSalePaymentIds: [],
        paymentId: uuid(),
      })
    },
    [
      outlet.currency,
      register.id,
      setPaymentData,
      setShowPartialAuthModal,
      tenderedAmount,
    ]
  )

  return { handleAcceptedPayment, handlePartialAuthRejected }
}
